import {
  Anchor,
  Badge,
  Button,
  Card,
  Group,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { UnifyTable, type UnifyTableColumn } from '@/components/UnifyTable';
import { AdjudicationSummary } from './components/AdjudicationSummary';
import { BillingBreadcrumb } from './components/BillingBreadcrumb';
import { formatCurrency } from './components/billingFormatters';
import { findDenialById } from './mock/mockDenials';
import { eobTotals, findEobById, type EobLineItem } from './mock/mockEobs';

export function EobDetailPage(): JSX.Element {
  const { eobId } = useParams();
  const eob = findEobById(eobId);
  const denial = eob?.denialId ? findDenialById(eob.denialId) : undefined;

  const lineColumns = useMemo(
    (): UnifyTableColumn<EobLineItem>[] => [
      { key: 'code', label: 'CPT', getValue: (row) => row.code },
      { key: 'description', label: 'Description', getValue: (row) => row.description },
      {
        key: 'billed',
        label: 'Billed',
        align: 'right',
        render: (row) => <Text size="sm">{formatCurrency(row.billed)}</Text>,
        getValue: (row) => String(row.billed),
      },
      {
        key: 'allowed',
        label: 'Allowed',
        align: 'right',
        render: (row) => <Text size="sm">{formatCurrency(row.allowed)}</Text>,
        getValue: (row) => String(row.allowed),
      },
      {
        key: 'paid',
        label: 'Paid',
        align: 'right',
        render: (row) => <Text size="sm">{formatCurrency(row.paid)}</Text>,
        getValue: (row) => String(row.paid),
      },
      {
        key: 'adjustment',
        label: 'Adjustment',
        align: 'right',
        render: (row) => <Text size="sm">{formatCurrency(row.adjustment)}</Text>,
        getValue: (row) => String(row.adjustment),
      },
      {
        key: 'patientResp',
        label: 'Patient Resp.',
        align: 'right',
        render: (row) => <Text size="sm">{formatCurrency(row.patientResp)}</Text>,
        getValue: (row) => String(row.patientResp),
      },
    ],
    [],
  );

  if (!eob) {
    return (
      <Stack gap="md" py="xl">
        <BillingBreadcrumb
          backTo="/dashboard/billing/remittances"
          backLabel="Back to remittances"
          crumbs={[
            { label: 'Billing', to: '/dashboard/billing' },
            { label: 'Remittances', to: '/dashboard/billing/remittances' },
            { label: 'EOB not found' },
          ]}
        />
        <Text c="dimmed">We couldn&apos;t find an EOB with id &ldquo;{eobId}&rdquo;.</Text>
      </Stack>
    );
  }

  const totals = eobTotals(eob);

  return (
    <Stack gap="lg">
      <BillingBreadcrumb
        backTo="/dashboard/billing/remittances"
        backLabel="Back to remittances"
        crumbs={[
          { label: 'Billing', to: '/dashboard/billing' },
          { label: 'Remittances', to: '/dashboard/billing/remittances' },
          { label: eob.id },
        ]}
      />

      <Group justify="space-between" align="flex-start">
        <Stack gap={4}>
          <Title order={3}>{eob.clientName}</Title>
          <Text size="sm" c="dimmed">
            {eob.payer} · Claim {eob.claimId}
          </Text>
        </Stack>
        <Badge
          variant="light"
          color={eob.outcome === 'paid' ? 'green' : eob.outcome === 'partial' ? 'yellow' : 'red'}
        >
          {eob.outcome}
        </Badge>
      </Group>

      <Group gap="sm">
        <Button
          component={Link}
          to={`/dashboard/billing/claims/${encodeURIComponent(eob.claimId)}`}
          variant="light"
        >
          View Claim
        </Button>
        {denial && (
          <Button component={Link} to="/dashboard/billing/remittances?outcome=denied" variant="light">
            View in Remittances
          </Button>
        )}
      </Group>

      <AdjudicationSummary {...totals} />

      <Card withBorder padding="md">
        <UnifyTable
          withPaper={false}
          title="Line Adjudication"
          columns={lineColumns}
          data={eob.lineItems}
          getRowKey={(row) => row.code}
          searchable={false}
          sortable={false}
          pagination={false}
        />
      </Card>

      {(eob.carcCodes.length > 0 || eob.rarcCodes.length > 0) && (
        <Card withBorder padding="md">
          <Stack gap="xs">
            <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
              Adjustment / Denial Codes
            </Text>
            {eob.carcCodes.length > 0 && (
              <Text size="sm">
                CARC: {eob.carcCodes.join(', ')}
              </Text>
            )}
            {eob.rarcCodes.length > 0 && (
              <Text size="sm">
                RARC: {eob.rarcCodes.join(', ')}
              </Text>
            )}
            {denial && (
              <Anchor component={Link} to="/dashboard/billing/remittances?outcome=denied" size="sm" fw={600}>
                {denial.denialReason}
              </Anchor>
            )}
          </Stack>
        </Card>
      )}
    </Stack>
  );
}
